import React from 'react';
import { Briefcase } from 'lucide-react';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency as formatCurrencyUtil } from '@/lib/utils/currency';

interface BrandCampaignsTabProps {
  brandCampaigns: any[];
  loadingCampaigns: boolean;
  handleViewCampaigns: () => void;
  handleCampaignClick: (id: string) => void;
  formatCurrency?: (amount: number | string) => string;
  navigate: (path: string) => void;
}

export const BrandCampaignsTab: React.FC<BrandCampaignsTabProps> = ({
  brandCampaigns,
  loadingCampaigns,
  handleViewCampaigns,
  handleCampaignClick,
  formatCurrency,
  navigate,
}) => {
  const { userCountry } = useAuth();

  const displayCurrency = (amount: number | string) => {
    if (formatCurrency) return formatCurrency(amount);
    return formatCurrencyUtil(amount, userCountry);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Campaigns</h3>
        <Button onClick={handleViewCampaigns}>
          View All Campaigns
        </Button>
      </div>

      {loadingCampaigns ? (
        <div className="text-center py-8 text-gray-500">Loading campaigns...</div>
      ) : brandCampaigns && brandCampaigns.length > 0 ? (
        <div className="space-y-3">
          {brandCampaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => handleCampaignClick(campaign.id)}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-gray-900 hover:text-purple-600">{campaign.name}</h4>
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                    <StatusBadge status={campaign.status} />
                    {campaign.start_date && (
                      <span>{new Date(campaign.start_date).toLocaleDateString()}</span>
                    )}
                    {campaign.end_date && (
                      <span>- {new Date(campaign.end_date).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Budget</p>
                  <p className="font-medium">
                    {campaign.total_budget ? displayCurrency(campaign.total_budget) : 'Not set'}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Campaigns</h3>
          <p className="text-gray-600 mb-4">This brand doesn't have any campaigns yet</p>
          <Button onClick={() => navigate('/campaigns')}>
            Create Campaign
          </Button>
        </div>
      )}
    </div>
  );
};

